/**
 * Organization invitation acceptance.
 *
 * A WorkOS invitation link lands on the app with only
 * `?invitation_token=<token>`: the token names its organization itself, and
 * AuthKit redeems it when sign-in passes it through as `invitationToken`. The
 * app owns that hop: drop the token and sign-in is an ordinary one that never
 * redeems the invitation.
 */

export interface InvitationParams {
  /** The WorkOS invitation token. */
  invitation: string;
}

const INVITATION_PARAM_KEYS = ['invitation_token'] as const;

function trimmed(value: string | null): string | undefined {
  const next = value?.trim();
  return next ? next : undefined;
}

/**
 * Invitation parameters from a query string, or null when this is an ordinary
 * page load.
 */
export function readInvitationParams(search: string): InvitationParams | null {
  const invitationToken = trimmed(new URLSearchParams(search).get('invitation_token'));
  return invitationToken ? { invitation: invitationToken } : null;
}

/**
 * The same location with the invitation parameters removed, so a redeemed (or
 * abandoned) token does not sit in the address bar and get replayed on reload.
 */
export function withoutInvitationParams(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of INVITATION_PARAM_KEYS) params.delete(key);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}
